import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import ToolsIcon from "@material-ui/icons/BuildOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useProject } from "../../context/Project/ProjectContext";
import { useConfirm } from "../../components/ConfirmDialog";

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg },
  wrap: { width: "100%", maxWidth: 1000, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  header: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: theme.spacing(2.5) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: theme.app.ink },
  subtitle: { color: theme.app.textTertiary, fontSize: 13, marginTop: 4 },
  grow: { flexGrow: 1 },
  tbl: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 10, overflow: "hidden" },
  row: { display: "flex", alignItems: "center", gap: 12, padding: "0 16px", height: 52, borderBottom: `1px solid ${theme.app.lineSoft}`, cursor: "pointer", "&:hover": { background: theme.app.hover }, "&:hover $act": { visibility: "visible" }, "&:last-child": { borderBottom: "none" } },
  ic: { width: 30, height: 30, borderRadius: 8, background: theme.app.lineSoft, display: "flex", alignItems: "center", justifyContent: "center", color: theme.app.textSecondary, flexShrink: 0, "& svg": { fontSize: 17 } },
  name: { fontSize: 13.5, color: theme.app.ink, fontWeight: 500 },
  desc: { fontSize: 12, color: theme.app.textTertiary, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 520 },
  pub: { fontFamily: theme.app.mono, fontSize: 11, padding: "2px 7px", borderRadius: 5 },
  pubOn: { background: "#E6F4EC", color: theme.app.ok },
  pubOff: { background: "#FBEFE0", color: theme.app.warn },
  act: { visibility: "hidden", color: theme.app.textTertiary },
  empty: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 10, padding: theme.spacing(6, 2), textAlign: "center", color: theme.app.textTertiary, fontSize: 13, lineHeight: 1.6 },
}));

const ToolsList = () => {
  const classes = useStyles();
  const history = useHistory();
  const { confirm } = useConfirm();
  const { activeProjectId } = useProject();
  const [tools, setTools] = useState([]);

  const load = useCallback(async () => {
    if (!activeProjectId) return;
    try {
      const { data } = await api.get("/applications", { params: { projectId: activeProjectId } });
      setTools((data.applications || []).filter(a => a.triggerType === "tool"));
    } catch (err) {
      toastError(err);
    }
  }, [activeProjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = () => {
    if (!activeProjectId) return;
    history.push(`/applications/new?projectId=${activeProjectId}&kind=tool`);
  };
  const open = t => history.push(`/applications/${t.code || t.id}?kind=tool`);

  const remove = async (e, t) => {
    e.stopPropagation();
    if (!(await confirm({ title: "Remover ferramenta", message: `Remover a ferramenta "${t.name}"?`, confirmText: "Remover", danger: true }))) return;
    try {
      await api.delete(`/applications/${t.id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <div>
            <button type="button" onClick={() => history.push("/agents")} style={{ display: "inline-flex", alignItems: "center", background: "none", border: "none", padding: 0, marginBottom: 8, fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: "#929AA4", cursor: "pointer" }}>← Agentes de IA</button>
            <Typography className={classes.title}>Ferramentas</Typography>
            <Typography className={classes.subtitle}>
              Funções que a IA pode chamar durante a conversa (ex.: consultar agenda, criar chamado). Cada
              ferramenta é um fluxo com entrada e retorno — desenhado no mesmo canvas.
            </Typography>
          </div>
          <div className={classes.grow} />
          <Button variant="contained" color="primary" disableElevation startIcon={<AddIcon />} onClick={create} disabled={!activeProjectId}>
            Nova ferramenta
          </Button>
        </div>

        {tools.length === 0 ? (
          <div className={classes.empty}>
            Nenhuma ferramenta ainda.<br />
            Crie a primeira para dar uma capacidade ao agente (ex.: <b>consultar_agenda</b>).
          </div>
        ) : (
          <div className={classes.tbl}>
            {tools.map(t => (
              <div key={t.id} className={classes.row} onClick={() => open(t)}>
                <div className={classes.ic}><ToolsIcon /></div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className={classes.name}>{t.name}</div>
                  {t.description ? <div className={classes.desc}>{t.description}</div> : null}
                </div>
                <span className={`${classes.pub} ${t.publishedVersion ? classes.pubOn : classes.pubOff}`}>
                  {t.publishedVersion ? `publicada v${t.publishedVersion}` : "rascunho"}
                </span>
                <span className={classes.act}>
                  <Tooltip title="Remover">
                    <IconButton size="small" onClick={e => remove(e, t)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolsList;
